import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { FaExclamationTriangle, FaUpload, FaTimes } from 'react-icons/fa';

const DisputeModal = ({ 
  projectId, 
  milestoneId = null, 
  authInfo, 
  userRole, // 'client' or 'seller'
  againstUserId, // ID of the user the dispute is against
  againstUserName = '', // Name of user dispute is against
  againstUserModel,
  existingDispute = null, // Add this prop to check for existing disputes
  onDisputeFiled 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [projectDetails, setProjectDetails] = useState(null);
  const [hasExistingDispute, setHasExistingDispute] = useState(false);
  const [existingDisputeData, setExistingDisputeData] = useState(null);

  // Check for existing disputes when component mounts or props change
  useEffect(() => {
    if (existingDispute) {
      setHasExistingDispute(true);
      setExistingDisputeData(existingDispute);
    } else {
      checkExistingDispute();
    }
  }, [projectId, milestoneId, existingDispute]);

  // Check for existing disputes
  const checkExistingDispute = async () => {
    try {
      const endpoint = userRole === 'client' ? '/user/disputes' : '/seller/disputes';
      
      // Prepare parameters based on user role
      const params = {
        projectId: projectId,
        milestoneId: milestoneId || undefined,
        status: 'open,under-review' // Only check for active disputes
      };

      // Add the required user ID parameter based on role
      if (userRole === 'client') {
        params.userId = authInfo.id;
      } else {
        params.sellerId = authInfo.id;
      }

      const response = await axios.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${authInfo.token}`
        },
        params: params
      });

      if (response.data.status && response.data.data.length > 0) {
        // Found existing dispute
        const existingDispute = response.data.data[0];
        setHasExistingDispute(true);
        setExistingDisputeData(existingDispute);
      } else {
        setHasExistingDispute(false);
        setExistingDisputeData(null);
      }
    } catch (error) {
      console.error('Error checking existing disputes:', error);
      if (error.response?.data?.message) {
        console.error('API Error:', error.response.data.message);
      }
      // If API fails, allow user to try filing dispute
      setHasExistingDispute(false);
    }
  };

  // Get dispute status info
  const getDisputeStatusInfo = () => {
    if (!hasExistingDispute) return null;
    
    const dispute = existingDisputeData;
    return {
      code: dispute?.disputeCode || 'Unknown',
      status: dispute?.status || 'Unknown',
      createdAt: dispute?.createdAt ? new Date(dispute.createdAt).toLocaleDateString() : 'Unknown',
      raisedBy: dispute?.raisedBy || 'Unknown'
    };
  };

  // Fetch project details when modal opens
  useEffect(() => {
    if (showModal && projectId) {
      fetchProjectDetails();
    }
  }, [showModal, projectId]);

  const fetchProjectDetails = async () => {
    try {
      // Use appropriate endpoint based on user role
      const endpoint = userRole === 'client' ? '/user/projects' : '/seller/projects';
      const response = await axios.get(`${endpoint}/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${authInfo.token}`
        }
      });
      setProjectDetails(response.data.data);
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  // Handle file upload with dynamic endpoint
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate file types and sizes
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File type ${file.type} is not supported`);
        return;
      }
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is 5MB`);
        return;
      }
    }

    setUploadingFiles(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        // Use appropriate upload endpoint based on user role
        const uploadEndpoint = userRole === 'client' 
          ? '/user/upload-dispute-attachment' 
          : '/seller/upload-dispute-attachment';
        
        const response = await axios.post(uploadEndpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authInfo.token}`
          }
        });
        
        return {
          url: response.data.url,
          name: file.name,
          type: file.type
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setAttachments(prev => [...prev, ...uploadedFiles]);
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploadingFiles(false);
    }
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Submit dispute with proper endpoint and data structure
  const handleSubmitDispute = async () => {
    if (!disputeReason.trim()) {
      toast.error('Please provide a reason for the dispute');
      return;
    }

    if (!againstUserId) {
      toast.error('Missing user information to file dispute against');
      return;
    }

    setLoading(true);
    try {
      const againstUserModel = userRole === 'client' ? 'Seller' : 'User';
      const disputeData = {
        projectId,
        milestoneId: milestoneId || null,
        raisedBy: userRole, // 'client' or 'seller'
        againstUser: againstUserId, // Using againstUser as per your API
        resolutionDetails: disputeReason,
        againstUserModel, // Using resolutionDetails as per your API
        attachments
      };

      // Use appropriate endpoint based on user role
      const endpoint = userRole === 'client' 
        ? '/user/dispute/create' 
        : '/seller/dispute/create';

      const response = await axios.post(endpoint, disputeData, {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          'Authorization': `Bearer ${authInfo.token}`,
          'Accept': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success('Dispute filed successfully. Our team will review it shortly.');
        setShowModal(false);
        setDisputeReason('');
        setAttachments([]);
        
        // Update local state to show dispute is filed
        setHasExistingDispute(true);
        setExistingDisputeData(response.data.dispute);
        
        // Callback to parent component
        if (onDisputeFiled) {
          onDisputeFiled(response.data.dispute);
        }
      } else {
        toast.error('Failed to file dispute');
      }
    } catch (error) {
      console.error('Dispute filing error:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to file dispute. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get display names for clarity
  const getCurrentUserRole = () => {
    return userRole === 'client' ? 'Client' : 'Seller';
  };

  const getAgainstUserRole = () => {
    return userRole === 'client' ? 'Seller' : 'Client';
  };

  return (
    <>
      {/* Dispute File Button */}
      {hasExistingDispute ? (
        // Show dispute status when dispute already exists
        <Button 
          variant="secondary" 
          size="sm"
          disabled
          className="d-flex align-items-center"
          title={`Dispute already filed - ${getDisputeStatusInfo()?.code}`}
        >
          <FaExclamationTriangle className="me-2" />
          Dispute Filed
        </Button>
      ) : (
        // Show file dispute button when no existing dispute
        <Button 
          variant="danger" 
          size="sm"
          onClick={() => setShowModal(true)}
          className="d-flex align-items-center"
        >
          <FaExclamationTriangle className="me-2" />
          File Dispute
        </Button>
      )}

      {/* Dispute Filing Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FaExclamationTriangle className="me-2" />
            File a Dispute
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {/* Show existing dispute warning if found during modal open */}
          {hasExistingDispute && (
            <Alert variant="warning">
              <strong>Dispute Already Exists!</strong><br/>
              A dispute has already been filed for this {milestoneId ? 'milestone' : 'project'}.<br/>
              <strong>Dispute Code:</strong> {getDisputeStatusInfo()?.code}<br/>
              <strong>Status:</strong> {getDisputeStatusInfo()?.status}<br/>
              <strong>Filed On:</strong> {getDisputeStatusInfo()?.createdAt}<br/>
              You cannot file another dispute until this one is resolved.
            </Alert>
          )}

          {/* Only show form if no existing dispute */}
          {!hasExistingDispute && (
            <>
              {/* Dispute Information Alert */}
              <Alert variant="info">
                <div className="row">
                  <div className="col-md-6">
                    <strong>Filed By:</strong> {getCurrentUserRole()} ({authInfo.name || authInfo.email})
                  </div>
                  <div className="col-md-6">
                    <strong>Against:</strong> {getAgainstUserRole()} ({againstUserName || 'Other Party'})
                  </div>
                </div>
                {projectDetails && (
                  <div className="mt-2">
                    <strong>Project:</strong> {projectDetails.title}
                    {milestoneId && <span> - Milestone Specific</span>}
                  </div>
                )}
              </Alert>

              <Alert variant="warning">
                <strong>Important:</strong> Filing a dispute will lock payments for this {milestoneId ? 'milestone' : 'project'} 
                until our admin team reviews and resolves the issue. Please provide detailed information.
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Dispute Reason *</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                    placeholder="Please describe the issue in detail. Include specific problems, timeline expectations, communication issues, or any other relevant information..."
                    required
                  />
                  <Form.Text className="text-muted">
                    Be as detailed as possible. This will help our team understand and resolve the issue faster.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Supporting Documents (Optional)</strong></Form.Label>
                  <Form.Control
                    type="file"
                    multiple
                    accept="image/*,.pdf,.txt"
                    onChange={handleFileUpload}
                    disabled={uploadingFiles}
                  />
                  <Form.Text className="text-muted">
                    Upload screenshots, contracts, messages, or other relevant files (Max 5MB each, PDF/Images/Text only)
                  </Form.Text>
                  
                  {uploadingFiles && (
                    <div className="mt-2">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Uploading files...
                    </div>
                  )}
                </Form.Group>

                {/* Attachment List */}
                {attachments.length > 0 && (
                  <div className="mb-3">
                    <Form.Label><strong>Attached Files:</strong></Form.Label>
                    <div className="border p-2 rounded">
                      {attachments.map((file, index) => (
                        <div key={index} className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-truncate">
                            <FaUpload className="me-2 text-muted" />
                            {file.name}
                          </span>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => removeAttachment(index)}
                          >
                            <FaTimes />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Alert variant="info">
                  <strong>What happens next:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Your dispute will be reviewed by our admin team</li>
                    <li>Payments for this {milestoneId ? 'milestone' : 'project'} will be held in escrow</li>
                    <li>Both parties may be contacted for additional information</li>
                    <li>A resolution decision will be made within 5-7 business days</li>
                    <li>You will receive notifications about the dispute status via email</li>
                  </ul>
                </Alert>
              </Form>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowModal(false)}
            disabled={loading}
          >
            {hasExistingDispute ? 'Close' : 'Cancel'}
          </Button>
          {!hasExistingDispute && (
            <Button 
              variant="danger" 
              onClick={handleSubmitDispute}
              disabled={loading || !disputeReason.trim()}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Filing Dispute...
                </>
              ) : (
                'File Dispute'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DisputeModal;